import type { CSSProperties, ReactNode } from 'react';

import { ApiError } from '../api/games.js';
import { isDemoRequested } from '../demo/config.js';
import { errorTitle } from './errors.js';
import type { BsTheme } from '../theme/useTheme.js';

/**
 * Chrome shared by every page inside the console shell — the classification
 * bar, the theme toggle, and the one error-note component every page uses so
 * a `correlationId` (§16.3) is never dropped on the floor by a one-off banner.
 */

/**
 * `height` is a parameter rather than a constant because the two places this
 * bar appears are drawn at two heights: entry and seat select at 26, the
 * console shell's own strip at the design's 20. One component, two callers,
 * neither of them redrawing the other's band.
 */
/**
 * The offline demo marks itself here rather than anywhere else on screen.
 *
 * A classification strip is exactly where "what kind of session is this" belongs,
 * and one more clause on it costs no layout and no reading. The flag is read from
 * the URL rather than threaded as a prop because it is a fact about the page load,
 * true for every screen at once, and unchanging (`demo/config.ts`).
 */
export function ClassificationBar(props: { right?: ReactNode; height?: number }): JSX.Element {
  return (
    <div
      style={{
        flex: 'none',
        height: props.height ?? 26,
        position: 'relative',
        background: 'var(--class-bar-bg)',
        color: 'var(--class-bar-text)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-data)',
        fontSize: 10,
        letterSpacing: '.24em',
        textTransform: 'uppercase',
      }}
    >
      {isDemoRequested() ? 'UNCLASSIFIED // EXERCISE // DEMO' : 'UNCLASSIFIED // EXERCISE'}
      {props.right}
    </div>
  );
}

/**
 * `inline` places the button in normal flow on a surface, rather than pinned to
 * the classification strip. Entry and seat select pin it; the console shell's
 * identity row carries it inline, as the design draws it — same control, same
 * behaviour, two mountings.
 */
export function ThemeToggleButton(props: {
  theme: BsTheme;
  onToggle: () => void;
  inline?: boolean;
}): JSX.Element {
  const pinned: CSSProperties = {
    position: 'absolute',
    top: 3,
    right: 14,
    zIndex: 'var(--z-pinned)',
    width: 20,
    height: 20,
    border: '1px solid rgba(255,255,255,.34)',
    color: 'rgba(255,255,255,.80)',
  };
  const inline: CSSProperties = {
    width: 24,
    height: 24,
    border: '1px solid var(--border)',
    color: 'var(--text-muted)',
  };

  return (
    <button
      type="button"
      onClick={props.onToggle}
      title="Toggle light / dark"
      aria-label="Toggle light / dark theme"
      data-testid="theme-toggle"
      style={{
        flex: 'none',
        background: 'transparent',
        fontFamily: 'var(--font-data)',
        fontSize: props.inline ? 11 : 10,
        lineHeight: 1,
        padding: 0,
        ...(props.inline ? inline : pinned),
      }}
    >
      {props.theme === 'dark' ? '◑' : '◐'}
    </button>
  );
}

/**
 * The console header's metrics.
 *
 * Shared rather than repeated because entry's closing beat *draws seat select's
 * header* — the same bars, the same crest, in the same place — and then cuts to
 * the real one. Two copies of these numbers is two chances for that cut to land
 * a few pixels off, which is the whole of what makes a match-cut read as a jump.
 */
/**
 * 84 rather than 66: the side crest is the one piece of scenario art on this
 * screen and at 41px it was a favicon. It is a header element, not a backdrop —
 * bigger, still in the bar, still bounded by its frame.
 */
export const CONSOLE_HEADER_H = 84;
export const CONSOLE_TABS_H = 39;
export const CONSOLE_PAD_X = 20;

/**
 * The side crest cluster that sits at the right of the console header: name,
 * identity, accent rule, crest. Entry settles this into place and seat select
 * opens with it already there — one component, so they cannot disagree.
 */
export function SideCrestCluster(props: {
  name: string;
  identity: string;
  accent: string;
  /** The crest itself — an `ImageSlot`, or nothing while there is no art to draw. */
  crest: ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        height: 62,
        borderRight: `2px solid ${props.accent}`,
        paddingRight: 13,
      }}
    >
      <div style={{ textAlign: 'right' }}>
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontWeight: 700,
            fontSize: 21,
            lineHeight: 1,
            letterSpacing: '.10em',
            textTransform: 'uppercase',
            color: props.accent,
          }}
        >
          {props.name}
        </div>
        <div
          style={{
            fontFamily: 'var(--font-data)',
            fontSize: 10,
            lineHeight: 1,
            letterSpacing: '.14em',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            marginTop: 5,
          }}
        >
          {props.identity}
        </div>
      </div>
      <div
        style={{
          flex: 'none',
          boxSizing: 'border-box',
          width: 62,
          height: 62,
          padding: 6,
          background: 'var(--surface-3)',
          border: '1px solid var(--border)',
        }}
      >
        {props.crest}
      </div>
    </div>
  );
}

/**
 * Every `ErrorCode` (§errors.ts) surfaces through this, with its correlationId
 * when present. Two tones, as the entry mockup draws them: `warning` for what
 * the player can fix in place (an incomplete field), `error` for what the
 * server refused.
 *
 * **A plain string with no `title` is the title.** Everything this app detects
 * for itself is one condition the copy rules give one uppercase line — CODE
 * INCOMPLETE — and routing those through `message` is what produced a banner
 * headed `ERROR` restating the rule that had just been broken. Pass a `title`
 * as well and the string goes back to being the sentence.
 */
export function ErrorNote(props: {
  error?: ApiError | Error | string | null;
  tone?: 'error' | 'warning';
  /** Overrides the table's title — used where the design names the failure itself. */
  title?: string;
}): JSX.Element | null {
  const { error } = props;
  if (!error && props.title === undefined) return null;

  const local = typeof error === 'string' ? error : null;
  const code = error instanceof ApiError ? error.code : null;
  const title = props.title ?? (code === null ? (local ?? 'ERROR') : errorTitle(code));
  // A bare string with no title is the title — one uppercase condition line, the
  // whole of what the copy rules give a failure this screen detected itself.
  // With a title beside it, it is the sentence: that is how the alert lane
  // passes a launch release, which has a designed heading and a real body.
  const message =
    props.title === undefined && local !== null
      ? ''
      : error instanceof Error
        ? error.message
        : (local ?? '');
  const correlationId = error instanceof ApiError ? error.correlationId : undefined;
  const warning = props.tone === 'warning' || (local !== null && props.tone === undefined);
  const edge = warning ? 'var(--warn)' : 'var(--red)';

  return (
    <div
      role="alert"
      data-testid="error-note"
      style={{
        background: warning ? 'var(--warn-tint)' : 'var(--red-surface, var(--red-tint))',
        border: `1px solid ${warning ? 'var(--warn-tint-border)' : 'var(--red)'}`,
        padding: '9px 12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 7, height: 7, background: edge, display: 'inline-block' }} />
        <span
          style={{
            fontFamily: 'var(--font-data)',
            fontSize: 11,
            letterSpacing: '.12em',
            color: warning ? 'var(--warn)' : 'var(--red-text, var(--red))',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </span>
      </div>
      {message !== '' && (
        <div
          style={{
            fontFamily: 'var(--font-prose)',
            fontSize: 12.5,
            lineHeight: 1.4,
            color: 'var(--text-2)',
            marginTop: 3,
          }}
        >
          {message}
        </div>
      )}
      {correlationId && (
        <div
          style={{
            fontFamily: 'var(--font-data)',
            fontSize: 10,
            letterSpacing: '.06em',
            color: 'var(--text-muted)',
            marginTop: 5,
          }}
        >
          correlation {correlationId}
        </div>
      )}
    </div>
  );
}

/**
 * The frame both terminal screens sit in — one screen, one sentence, one way out.
 *
 * Terminal means terminal: there is no retry button, because there is nothing to
 * retry. Offering one would invite the exact loop these screens exist to stop.
 */
function TerminalScreen(props: {
  title: string;
  body: ReactNode;
  /** Omitted where there is nothing the reader can press — see `ConfigErrorScreen`. */
  actionLabel?: string;
  onAction?: () => void;
  testId: string;
}): JSX.Element {
  return (
    <div
      data-bs-theme="dark"
      data-testid={props.testId}
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        background: 'var(--bg)',
        color: 'var(--text)',
        fontFamily: 'var(--font-prose)',
        textAlign: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-ui)',
          fontWeight: 700,
          fontSize: 22,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--red)',
        }}
      >
        {props.title}
      </div>
      <p style={{ maxWidth: 440, color: 'var(--text-2)', fontSize: 14, lineHeight: 1.5 }}>
        {props.body}
      </p>
      {props.actionLabel !== undefined && (
        <button
          type="button"
          onClick={props.onAction}
          style={{
            height: 44,
            padding: '0 22px',
            background: 'var(--primary)',
            color: 'var(--primary-on)',
            border: '1px solid var(--primary)',
            fontWeight: 600,
            fontSize: 12,
            // .14em: .16em is licensed for the two 50px entry buttons, and this
            // is a 44px action inside a terminal screen.
            letterSpacing: '.14em',
            textTransform: 'uppercase',
          }}
        >
          {props.actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * The SPA was built without `VITE_API_BASE_URL` (§2.1) — it cannot reach any
 * server, and no button here changes that.
 *
 * On the same frame as the other two terminal screens rather than M0's Tailwind
 * panel, which carried no theme root: `bg-neutral-900/60` over a light document
 * rendered as unstyled text on white, which is not the impression a
 * misconfigured deploy should make on whoever opens it.
 */
export function ConfigErrorScreen(props: { message: string }): JSX.Element {
  return <TerminalScreen testId="config-error" title="Not configured" body={props.message} />;
}

/**
 * The seat's epoch is stale (shell.md §Connection, decision M2-D8).
 *
 * Reachable two ways — a GM force-release, or the same seat claimed from another
 * device — and until this phase it had never been *seen*: the socket read no
 * close code, so a 1008 looked like a network blip and the client re-attacked a
 * dead token on a backoff, forever.
 *
 * Terminal, and the only route back is entry. There is deliberately no retry:
 * the token cannot become current again, and somebody else may now be sitting
 * in the seat.
 */
export function SupersededScreen(props: { reason: string; onExit: () => void }): JSX.Element {
  return (
    <TerminalScreen
      testId="superseded"
      title="Seat released"
      body={props.reason}
      actionLabel="Return to entry"
      onAction={props.onExit}
    />
  );
}

/**
 * 426 — the server was redeployed underneath this client. Terminal; needs a
 * hard reload.
 *
 * Two callers, two ways of knowing. `App.tsx` compares protocol numbers against
 * `/api/version` before the lobby and has both to name. The seat socket finds it
 * on an open seat — a tab that has been sitting on a chair since before the
 * deploy, which the proactive check never sees — and there it may be the *rule
 * content* that moved rather than the protocol number, so it supplies the
 * sentence itself.
 */
export type ProtocolMismatchProps =
  | { serverVersion: number; clientVersion: number; detail?: undefined }
  | { detail: string; serverVersion?: undefined; clientVersion?: undefined };

export function ProtocolMismatchScreen(props: ProtocolMismatchProps): JSX.Element {
  return (
    <TerminalScreen
      testId="protocol-mismatch"
      title="Server updated"
      body={
        // One sentence, and it is the action. What moved, and that the old page
        // is fine, is the mechanism narration the copy rules cut.
        props.detail ?? 'Reload to pick up the matching client.'
      }
      actionLabel="Reload"
      onAction={() => window.location.reload()}
    />
  );
}

/**
 * `inert` on a subtree, for a React 18 that has no typed prop for it.
 *
 * `pointer-events: none` does **not** take an element out of the tab order.
 * Entry keeps all four of its stages mounted and hides three of them with
 * opacity, so on first paint the first three tab stops were the three side
 * buttons at opacity 0 — a keyboard user tabbed three times into nothing before
 * reaching a visible control. It is also what stops a click landing on an
 * outgoing stage that is still on top of the one being clicked.
 */
export function inertUnless(active: boolean): Record<string, string> {
  return active ? {} : { inert: '' };
}
